import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { MatMenuModule } from "@angular/material/menu";
import { MatButtonModule } from "@angular/material/button";
import {
  faEnvelope,
  faEdit,
  faLock,
  faBars,
  faXmark,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";

import { ModalService } from "../shared/services/modal.service";
import { GlobalService } from "../shared/services/global.service";
import { LocaleService } from "../shared/services/locale.service";
import { AuthenticationService } from "../shared/services/authentication.service";
import { UserService } from "../shared/services/user.service";
import { DynamicRouteService } from "src/app/shared/services/dynamic-route.service";
import { RoleTypes } from "../guards/roletypes";
import { passwordResetUrl } from "../auth.config";
import { studentprofilePlaceholder } from "../model/model.studentprofile";
import { ModelUser } from "../model/model.user";
import { empty } from "../functions";
import { NavItem, NavProfile, getNavItems } from "./nav-config";
import { NavDropdownComponent } from "./nav-dropdown.component";
import { NavMobileGroupComponent } from "./nav-mobile-group.component";

@Component({
  selector: "app-menu-header",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    FontAwesomeModule,
    MatMenuModule,
    MatButtonModule,
    NavDropdownComponent,
    NavMobileGroupComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    "(document:keydown.escape)": "closeMobileMenuOnEscape()",
  },
  styles: [
    `
      .nav-underline {
        position: absolute;
        bottom: 0;
        left: 50%;
        width: 0;
        height: 2px;
        background: #357bbf;
        transition: width 0.25s ease, left 0.25s ease;
      }

      .nav-link-standalone:hover .nav-underline,
      .nav-link-active .nav-underline {
        width: 60%;
        left: 20%;
      }

      .nav-link-active {
        color: #357bbf;
      }
    `,
  ],
  template: `
    <!-- Desktop + Mobile nav bar -->
    <nav class="flex items-center justify-between py-3 px-4 lg:px-6">
      <!-- Logo (left) -->
      <a class="flex-shrink-0" routerLink="/">
        <img
          class="h-[50px]"
          src="../../../assets/img/header-logo.png"
          alt="Logo"
        />
      </a>

      <!-- Desktop nav links (center) — hidden below lg -->
      <div
        class="hidden lg:flex items-center gap-1 font-alternate text-base text-mty-body"
      >
        @if (userService.currentUserIsAdmin()) {
        <a
          class="px-3 py-2 hover:text-mty-primary transition-colors"
          routerLink="/admin"
        >
          <fa-icon [icon]="faEdit" class="mr-1"></fa-icon
          >{{ "Management" | translate }}
        </a>
        } @for (item of navItems(); track item.labelKey) { @if (item.children) {
        <app-nav-dropdown [item]="item" [resolveRoute]="resolveRouteFn" />
        } @else if (item.standalone) {
        <a
          class="nav-link-standalone px-3 py-2 hover:text-mty-primary transition-colors relative"
          [routerLink]="resolveRoute(item)"
          [queryParams]="item.queryParams"
          routerLinkActive="nav-link-active"
        >
          {{ item.labelKey | translate }}
          <span class="nav-underline"></span>
        </a>
        } }
      </div>

      <!-- Right side actions -->
      <div class="flex items-center gap-2">
        <!-- Language switcher dropdown -->
        <button
          [matMenuTriggerFor]="langMenu"
          class="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <span class="text-2xl leading-none">{{
            currentLanguage().flag
          }}</span>
          <fa-icon
            [icon]="faChevronDown"
            class="text-xs text-gray-500"
          ></fa-icon>
        </button>
        <mat-menu #langMenu="matMenu">
          @for (language of supportedLanguages(); track language.code) {
          <button
            mat-menu-item
            (click)="localeService.setCurrentLanguage(language.code)"
          >
            <span class="text-lg mr-2">{{ language.flag }}</span>
            <span class="capitalize">{{ language.nativeName }}</span>
          </button>
          }
        </mat-menu>

        <!-- Student applications icon -->
        @if (loggedIn() && userService.currentUserHasRole(RoleTypes.Student)) {
        <a
          class="flex items-center px-2 py-1 text-mty-body hover:text-mty-primary transition-colors"
          routerLink="/student/applications"
        >
          <fa-icon [icon]="faEnvelope" class="text-lg"></fa-icon>
        </a>
        }

        <!-- Company applications icon -->
        @if (loggedIn() && userService.currentUserHasRole(RoleTypes.Company)) {
        <a
          class="flex items-center px-2 py-1 text-mty-body hover:text-mty-primary transition-colors"
          routerLink="/company/applications"
        >
          <fa-icon [icon]="faEnvelope" class="text-lg"></fa-icon>
        </a>
        }

        <!-- Not logged in — Login dropdown (click-only; Material handles open/close) -->
        @if (!loggedIn()) {
        <button
          [matMenuTriggerFor]="loginMenu"
          type="button"
          class="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 transition-colors font-bold cursor-pointer"
        >
          <fa-icon [icon]="faLock" class="text-sm"></fa-icon>
          <span class="font-alternate text-base">{{
            "Login" | translate
          }}</span>
          <fa-icon
            [icon]="faChevronDown"
            class="text-xs text-gray-500"
          ></fa-icon>
        </button>
        <mat-menu #loginMenu="matMenu">
          <button mat-menu-item class="nav-dropdown-item" (click)="Login()">
            {{ "Login" | translate }}
          </button>
          <a
            mat-menu-item
            class="nav-dropdown-item"
            routerLink="/registreren"
          >
            {{ "Register" | translate }}
          </a>
          <button
            mat-menu-item
            class="nav-dropdown-item"
            (click)="resetPassword()"
          >
            {{ "Reset password" | translate }}
          </button>
        </mat-menu>
        }

        <!-- Logged in — User dropdown -->
        @if (loggedIn()) {
        <button
          [matMenuTriggerFor]="userMenu"
          class="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
        >
          <span class="font-alternate text-base mr-1">{{
            "Me" | translate
          }}</span>
          @if (accountprofile().avatarUri) {
          <img
            class="w-8 h-8 rounded-full object-cover"
            [src]="accountprofile().avatarUri"
            [alt]="
              accountprofile().firstName
                ? accountprofile().firstName + ' ' + accountprofile().lastName
                : ''
            "
          />
          }
          <fa-icon
            [icon]="faChevronDown"
            class="text-xs text-gray-500"
          ></fa-icon>
        </button>
        <mat-menu #userMenu="matMenu">
          @if (userService.currentUserHasRole(RoleTypes.Student)) {
          <a mat-menu-item routerLink="/profile">
            {{ "My profile" | translate }}
          </a>
          <button mat-menu-item routerLink="/student/settings">
            {{ "Personal settings" | translate }}
          </button>
          } @if (userService.currentUserHasRole(RoleTypes.Company)) {
          <a mat-menu-item routerLink="/company/profile">
            {{ "My profile" | translate }}
          </a>
          } @if (userService.currentUserHasRole(RoleTypes.Company) ||
          userService.currentUserIsAdmin() ||
          userService.currentUserHasRole(RoleTypes.Teacher)) {
          <button mat-menu-item routerLink="/account">
            {{ "Account" | translate }}
          </button>
          } @if (userService.currentUserIsAdmin()) {
          <button mat-menu-item routerLink="/admin">
            {{ "Management" | translate }}
          </button>
          }
          <button mat-menu-item data-cy="logout" (click)="Logout()">
            {{ "Log out" | translate }}
          </button>
        </mat-menu>
        }

        <!-- Contact button -->
        <a
          class="hidden lg:inline-flex items-center flex-shrink-0 bg-mty-primary text-white rounded-[16px] px-3 py-2 font-alternate text-base hover:opacity-90 transition-opacity"
          [routerLink]="getLocalizedRoute('pages', ['contact'])"
        >
          {{ "Contact" | translate }}
        </a>

        <!-- Mobile hamburger toggle (visible below lg) -->
        <button
          class="lg:hidden flex items-center px-2 py-1 rounded hover:bg-gray-100 transition-colors cursor-pointer"
          type="button"
          [attr.aria-label]="
            mobileMenuOpen()
              ? ('Close menu' | translate)
              : ('Open menu' | translate)
          "
          [attr.aria-expanded]="mobileMenuOpen()"
          aria-controls="mobile-menu-panel"
          (click)="mobileMenuOpen.set(!mobileMenuOpen())"
        >
          @if (mobileMenuOpen()) {
          <fa-icon [icon]="faXmark" class="text-xl"></fa-icon>
          } @else {
          <fa-icon [icon]="faBars" class="text-xl"></fa-icon>
          }
        </button>
      </div>
    </nav>

    <!-- Mobile menu panel (shown when mobileMenuOpen) -->
    @if (mobileMenuOpen()) {
    <div
      id="mobile-menu-panel"
      class="lg:hidden border-t border-gray-200 bg-white px-4 py-3 font-alternate text-base text-mty-body"
    >
      @if (userService.currentUserIsAdmin()) {
      <a
        class="block py-2 hover:text-mty-primary transition-colors"
        routerLink="/admin"
        (click)="mobileMenuOpen.set(false)"
      >
        <fa-icon [icon]="faEdit" class="mr-1"></fa-icon
        >{{ "Management" | translate }}
      </a>
      } @for (item of navItems(); track item.labelKey) { @if (item.children) {
      <app-nav-mobile-group
        [item]="item"
        [resolveRoute]="resolveRouteFn"
        (navigate)="mobileMenuOpen.set(false)"
      />
      } @else if (item.standalone) {
      <a
        class="block py-2 hover:text-mty-primary transition-colors"
        [routerLink]="resolveRoute(item)"
        [queryParams]="item.queryParams"
        (click)="mobileMenuOpen.set(false)"
      >
        {{ item.labelKey | translate }}
      </a>
      } }
    </div>
    }
  `,
})
export class MenuHeaderComponent implements OnInit {
  localeService = inject(LocaleService);
  dynamicRouteService = inject(DynamicRouteService);
  modalService = inject(ModalService);
  global = inject(GlobalService);
  authService = inject(AuthenticationService);
  userService = inject(UserService);
  private destroyRef = inject(DestroyRef);

  passwordResetUrl = passwordResetUrl;
  avatarPlaceholder = studentprofilePlaceholder.avatarPlaceholder;

  // Icons
  faEnvelope = faEnvelope;
  faEdit = faEdit;
  faLock = faLock;
  faBars = faBars;
  faXmark = faXmark;
  faChevronDown = faChevronDown;

  loggedIn = signal(false);
  mobileMenuOpen = signal(false);
  RoleTypes = RoleTypes;

  readonly navProfile = computed<NavProfile>(() => {
    if (!this.loggedIn()) return "guest";
    if (this.userService.currentUserIsAdmin())
      return "admin";
    if (this.userService.currentUserHasRole(RoleTypes.Teacher))
      return "teacher";
    if (this.userService.currentUserHasRole(RoleTypes.Company))
      return "company";
    return "student";
  });

  readonly navItems = computed(() => getNavItems(this.navProfile()));

  readonly resolveRouteFn = (item: NavItem): string => this.resolveRoute(item);

  readonly accountprofile = signal<ModelUser>({
    id: "",
    firstName: "",
    lastName: "",
    emailAddress: "",
    phoneNumber: "",
    isSubscribedToNewsletter: false,
    allowEmailNotifications: false,
    avatarUri: "",
    preferredLanguage: "",
    hasAcceptedGdprPolicy: false,
    roles: [],
  });

  currentLanguage = this.localeService.currentLanguage;
  supportedLanguages = this.localeService.supportedLanguages;

  ngOnInit(): void {
    this.authService.isAuthenticated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((authenticated) => this.loggedIn.set(authenticated));

    this.authService.profile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result != null && !empty(result["avatarUri"])) {
          this.accountprofile.set(result);
        } else {
          this.accountprofile.update((p) => ({
            ...p,
            avatarUri: this.avatarPlaceholder,
          }));
        }
      });
  }

  closeMobileMenuOnEscape(): void {
    if (this.mobileMenuOpen()) {
      this.mobileMenuOpen.set(false);
    }
  }

  Login() {
    this.authService.Login();
  }

  Logout() {
    this.authService.Logout();
  }

  resetPassword() {
    window.location.href = passwordResetUrl;
  }

  resolveRoute(item: NavItem): string {
    if (item.route) return item.route;
    if (item.routeLocalized) {
      return this.dynamicRouteService.getLocalizedRoutePath(
        item.routeLocalized.key,
        item.routeLocalized.params ?? []
      );
    }
    return "/";
  }

  getLocalizedRoute(routeKey: string, params: string[] = []): string {
    return this.dynamicRouteService.getLocalizedRoutePath(routeKey, params);
  }
}
